import { useEffect, useMemo } from 'react';
import { Globe2 } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageProvider';
import type { AppLanguage } from '../../i18n/locale';
import { setDisplayCurrency, setRuntimeTimezone, type DisplayCurrency } from '../../i18n/preferences';
import './locale-settings-card.css';

export interface LocaleSettingsCardProps {
  className?: string;
  figmaNode?: string;
  timezone?: string;
  displayCurrency?: DisplayCurrency;
  onSaveLocale?: (patch: { timezone?: string; display_currency?: DisplayCurrency }) => void | Promise<void>;
}

const intlWithTimezones = Intl as typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

function timezoneOffset(timezone: string) {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

function timezoneLabel(timezone: string) {
  const offset = timezoneOffset(timezone).replace('GMT', 'UTC');
  const place = timezone.replaceAll('_', ' ').replace('/', ' / ');
  return offset ? `(${offset}) ${place}` : place;
}

export function LocaleSettingsCard({
  className = '',
  figmaNode = '2680:40913',
  timezone = 'Europe/Moscow',
  displayCurrency = 'RUB',
  onSaveLocale,
}: LocaleSettingsCardProps) {
  const { language, setLanguage } = useLanguage();
  const timezones = useMemo(() => {
    const supported = intlWithTimezones.supportedValuesOf?.('timeZone') ?? [
      'Europe/Kaliningrad',
      'Europe/Moscow',
      'Asia/Yekaterinburg',
      'Europe/Berlin',
      'Europe/Paris',
      'Asia/Almaty',
      'Asia/Tbilisi',
      'America/New_York',
    ];
    return Array.from(new Set(['UTC', ...supported, timezone])).sort();
  }, [timezone]);

  useEffect(() => {
    setRuntimeTimezone(timezone);
    setDisplayCurrency(displayCurrency);
  }, [displayCurrency, timezone]);

  return (
    <section
      id="settings-locale"
      className={`lovo-locale-card customer-settings__card is-locale ${className}`.trim()}
      data-figma-node={figmaNode}
    >
      <header className="lovo-locale-card__header">
        <h2>
          <Globe2 aria-hidden="true" size={20} />
          <span>Язык и регион</span>
        </h2>
      </header>
      <div className="lovo-locale-card__grid">
        <label className="lovo-locale-card__field">
          <span>Язык интерфейса</span>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value as AppLanguage)}
          >
            <option value="ru">Русский</option>
            <option value="en">English</option>
          </select>
          <small>Также применяется к письмам и SMS</small>
        </label>

        <label className="lovo-locale-card__field">
          <span>Валюта отображения</span>
          <select
            value={displayCurrency}
            onChange={(event) => {
              const currency = event.target.value as DisplayCurrency;
              setDisplayCurrency(currency);
              void onSaveLocale?.({ display_currency: currency });
            }}
          >
            <option value="RUB">{language === 'en' ? '₽ Russian rouble' : '₽ Российский рубль'}</option>
            <option value="EUR">{language === 'en' ? '€ Euro' : '€ Евро'}</option>
          </select>
          <small>Используется как валюта новых заявок и отображения сумм</small>
        </label>

        <label className="lovo-locale-card__field lovo-locale-card__field--full">
          <span>Часовой пояс</span>
          <select
            value={timezone ?? 'Europe/Moscow'}
            onChange={(event) => {
              setRuntimeTimezone(event.target.value);
              void onSaveLocale?.({ timezone: event.target.value });
            }}
          >
            {timezones.map((item) => <option key={item} value={item}>{timezoneLabel(item)}</option>)}
          </select>
          <small>Используется в датах контрольных точек и уведомлениях</small>
        </label>
      </div>
    </section>
  );
}
